import { Component, computed, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { FamilyAnalyseData } from '../../../models/FamilyAnalyseData';
import { AnalyseService } from '../../../services/analyse-service';
import { InviteAnalyse } from '../../../models/InviteAnalyse';
import { AttendingStatus } from '../../../models/AttendingStatus.enum';
import { attendingIconPipe } from "../../../pipes/attending-icon-pipe-pipe";
import { InvitationTypeIconPipe } from "../../../pipes/invitation-type-icon-pipe";
import { InvitationType } from '../../../models/InvitationType.enum';
import { form, FormField } from '@angular/forms/signals';
import { UpdateService } from '../../../services/update-service';
import { ModalService } from '../../common/modal/modal-service';
import { CreateNewFamily } from '../create-new-family/create-new-family';
import { take } from 'rxjs';
import { UpdateFamilyRsvp } from '../update-family-rsvp/update-family-rsvp';
import { Invitations } from '../invitations/invitations';

@Component({
  selector: 'app-display',
  imports: [attendingIconPipe, InvitationTypeIconPipe, FormField],
  templateUrl: './display.html',
  styleUrl: 'display.scss',
})
export class Display implements OnInit {

  private analyseService = inject(AnalyseService);
  private toastrService = inject(ToastrService);
  private updateService = inject(UpdateService);
  private modalService = inject(ModalService);
  showLoader = signal(false);

  families: WritableSignal<FamilyAnalyseData[]> = signal([]);

  invitationTypeFilter: WritableSignal<string> = signal('');
  responseFilter: WritableSignal<string> = signal('');
  canInviteCoupleFilter: WritableSignal<string> = signal('');
  canInviteChildrenFilter: WritableSignal<string> = signal('');
  isAdultFilter: WritableSignal<string> = signal('');
  staysAtBrunchFilter: WritableSignal<string> = signal('');
  queryFilter: WritableSignal<string> = signal('');
  invitationTypeFilterForm = form(this.invitationTypeFilter);
  responseFilterForm = form(this.responseFilter);
  canInviteCoupleFilterForm = form(this.canInviteCoupleFilter);
  canInviteChildrenFilterForm = form(this.canInviteChildrenFilter);
  isAdultFilterForm = form(this.isAdultFilter);
  staysAtBrunchFilterForm = form(this.staysAtBrunchFilter);
  queryFilterForm = form(this.queryFilter);

  datatableActiveSort: WritableSignal<DatatableSort> = signal({
    'order': 'ASC',
    'field': 'lastName'
  });
  datatable2ndSort = computed(
    () => {
      if (this.datatableActiveSort().field === 'lastName') {
        return 'firstName';
      } else {
        return 'lastName';
      }
    }
  );
  datatable3rdSort = computed(
    () => {
      if (this.datatableActiveSort().field === 'familyDepartement') {
        return 'firstName';
      } else {
        return 'familyDepartement';
      }
    }
  );

  inviteDataModel = computed(
    () => {
      let inviteTable: InviteAnalyse[] = [];
      this.families().forEach(family => {
        family.persons.forEach(
          person => {
            inviteTable.push({
              firstName: person.firstName,
              lastName: person.lastName.toUpperCase(),
              adult: person.adult,
              attendingStatus: family.attendingStatus,
              familyId: family.qrCodeId,
              canRequireChildsInvite: family.canRequireChildsInvite,
              canRequireCoupleInvite: family.canRequireCoupleInvite,
              invitationType: family.invitationType,
              familyDepartement: family.familyDepartement,
              staysForBrunch: family.staysForBrunch
            })
          }
        )
      });
      return inviteTable
        .filter(
          invite => {
            return invite.firstName.toUpperCase().includes(this.queryFilter().toUpperCase())
              || invite.lastName.toUpperCase().includes(this.queryFilter().toUpperCase())
              || invite.familyDepartement.includes(this.queryFilter())
          }
        ).filter(
          invite => this.invitationTypeFilter().length > 0 ? invite.invitationType === this.invitationTypeFilter() : true
        ).filter(
          invite => this.responseFilter().length > 0 ? invite.attendingStatus === this.responseFilter() : true
        ).filter(
          invite => this.canInviteCoupleFilter().length > 0 ? invite.canRequireCoupleInvite.toString() === this.canInviteCoupleFilter() : true
        ).filter(
          invite => this.canInviteChildrenFilter().length > 0 ? invite.canRequireChildsInvite.toString() === this.canInviteChildrenFilter() : true
        ).filter(
          invite => this.isAdultFilter().length > 0 ? invite.adult.toString() === this.isAdultFilter() : true
        ).filter(
          invite => this.staysAtBrunchFilter().length > 0 ? invite.staysForBrunch.toString() === this.staysAtBrunchFilter() : true
        ).sort(
          (a, b) => this.sortDatatable(a, b)
        );
    }
  );

  attendingStatusOptions = [
    AttendingStatus.NOT_RESPONDED,
    AttendingStatus.ATTENDING,
    AttendingStatus.NOT_ATTENDING,
    AttendingStatus.NOT_SURE_YET,
  ];
  attendingStatusEmpty = AttendingStatus.EMPTY;

  invitationTypesOptions = [
    InvitationType.EMPTY,
    InvitationType.FAMILLE,
    InvitationType.AMIS,
    InvitationType.AMIS_PARENTS,
    InvitationType.VH,
    InvitationType.VH_PARENTS
  ];

  booleanFilterOptions = [
    { 'key': "", 'value': '-----' },
    { 'key': "true", 'value': 'Oui' },
    { 'key': "false", 'value': 'Non' }
  ];

  ngOnInit(): void {
    this.refreshDataSet();
  }

  refreshDataSet(): void {
    this.showLoader.set(true);
    this.analyseService.getCompleteFamiliesData().then(
      response => {
        this.families.set(response.completeData);
      }
    ).catch(() => this.toastrService.error('Erreur de recuperation'))
      .finally(
        () => this.showLoader.set(false)
      );
  }

  resetFilters(): void {
    this.invitationTypeFilter.set('');
    this.responseFilter.set('');
    this.canInviteCoupleFilter.set('');
    this.canInviteChildrenFilter.set('');
    this.isAdultFilter.set('');
    this.queryFilter.set('');
    this.staysAtBrunchFilter.set('');
  }

  updateFamilyResponse(familyId: string, event: Event): void {
    const newAttendingResponse = AttendingStatus.retrieveKeyByValue((event.target as any).value);
    const family = this.families().filter(
      family => family.qrCodeId === familyId
    )[0];
    this.showLoader.set(true);
    this.updateService.saveFamilyAnalyseTableData(familyId, newAttendingResponse, family.canRequireCoupleInvite, family.canRequireChildsInvite, family.staysForBrunch)
      .then(
        response => {
          this.families.set(response.completeData);
        }
      ).catch(() => this.toastrService.error('Erreur de mise à jour'))
      .finally(
        () => this.showLoader.set(false)
      );
  }

  updateStaysForBrunch(familyId: string, event: Event): void {
    const newStaysForBrunch = (event.target as any).checked;
    const family = this.families().filter(
      family => family.qrCodeId === familyId
    )[0];
    this.showLoader.set(true);
    this.updateService.saveFamilyAnalyseTableData(familyId, family.attendingStatus, family.canRequireCoupleInvite, family.canRequireChildsInvite, newStaysForBrunch)
      .then(
        response => {
          this.families.set(response.completeData);
        }
      ).catch(() => this.toastrService.error('Erreur de mise à jour'))
      .finally(
        () => this.showLoader.set(false)
      );
  }

  updateRequireChildsInvite(familyId: string, event: Event): void {
    const newRequireChildsInvite = (event.target as any).checked;
    const family = this.families().filter(
      family => family.qrCodeId === familyId
    )[0];
    this.showLoader.set(true);
    this.updateService.saveFamilyAnalyseTableData(familyId, family.attendingStatus, family.canRequireCoupleInvite, newRequireChildsInvite, family.staysForBrunch)
      .then(
        response => {
          this.families.set(response.completeData);
        }
      ).catch(() => this.toastrService.error('Erreur de mise à jour'))
      .finally(
        () => this.showLoader.set(false)
      );
  }

  updateRequireCoupleInvite(familyId: string, event: Event): void {
    const newRequireCoupleInvite = (event.target as any).checked;
    const family = this.families().filter(
      family => family.qrCodeId === familyId
    )[0];
    this.showLoader.set(true);
    this.updateService.saveFamilyAnalyseTableData(familyId, family.attendingStatus, newRequireCoupleInvite, family.canRequireChildsInvite, family.staysForBrunch)
      .then(
        response => {
          this.families.set(response.completeData);
        }
      ).catch(() => this.toastrService.error('Erreur de mise à jour'))
      .finally(
        () => this.showLoader.set(false)
      );
  }

  createNewFamily(): void {
    this.modalService.open({
      component: CreateNewFamily,
      title: 'Nouvelle invitation',
      inputs: {},
      top: 50,
      admin: true,
      closeOnBackdrop: false
    }).pipe(
      take(1)
    ).subscribe(
      val => {
        if (val) {
          this.refreshDataSet();
        }
      }
    );
  }

  seeFamilyDetails(familyUuid: string): void {
    this.modalService.open({
      component: UpdateFamilyRsvp,
      title: 'Modifier une invitation',
      inputs: { familyUuid: familyUuid },
      top: 50,
      admin: true,
      closeOnBackdrop: false
    }).pipe(
      take(1)
    ).subscribe(
      val => {
        if (val) {
          this.refreshDataSet();
        }
      }
    );
  }

  openGenerateInvitationsModal(): void {
    this.modalService.open({
      component: Invitations,
      title: 'Generer les fair-parts d\'invitation',
      inputs: {
        visibleFamiliesUuid: this.inviteDataModel().map(
          fam => fam.familyId
        ),
        allFamiliesUuid: this.families().map(
          fam => fam.qrCodeId
        )
      },
      top: 50,
      admin: true,
      closeOnBackdrop: false
    });
  }

  sortDatatable(a: InviteAnalyse, b: InviteAnalyse): number {
    if (a[this.datatableActiveSort().field].toUpperCase() < b[this.datatableActiveSort().field].toUpperCase()) {
      return this.datatableActiveSort().order === 'ASC' ? -1 : 1;
    } else if (a[this.datatableActiveSort().field].toUpperCase() > b[this.datatableActiveSort().field].toUpperCase()) {
      return this.datatableActiveSort().order === 'ASC' ? 1 : -1;
    } else {
      if (a[this.datatable2ndSort()].toUpperCase() < b[this.datatable2ndSort()].toUpperCase()) {
        return -1;
      } else if (a[this.datatable2ndSort()].toUpperCase() > b[this.datatable2ndSort()].toUpperCase()) {
        return 1;
      } else {
        if (a[this.datatable3rdSort()].toUpperCase() < b[this.datatable3rdSort()].toUpperCase()) {
          return -1;
        } else if (a[this.datatable3rdSort()].toUpperCase() > b[this.datatable3rdSort()].toUpperCase()) {
          return 1;
        } else {
          return 0;
        }
      }
    }
  }

  updateSort(field: DatatableSort["field"]): void {
    if (this.datatableActiveSort().field === field) {
      this.datatableActiveSort.set(
        {
          field: this.datatableActiveSort().field,
          order: this.datatableActiveSort().order === 'ASC' ? 'DESC' : 'ASC'
        });
    } else {
      this.datatableActiveSort.set(
        {
          field,
          order: 'ASC'
        });
    }
  }
}

type DatatableSort = {
  'order': 'ASC' | 'DESC';
  'field': 'firstName' | 'lastName' | 'familyDepartement';
}
